import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { cmsTextClass, eyebrowClass, iconTileClass, sheetClass } from "../../styles";

const linkClass =
  "rounded text-ink-700 underline-offset-4 transition-colors hover:text-[var(--site1-primary-strong)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--site1-primary-strong)] motion-reduce:transition-none";

const Item = ({ icon, children }) => (
  <li className="flex min-w-0 items-start gap-3">
    <span className={iconTileClass} aria-hidden="true">
      <i className={`bi ${icon}`} />
    </span>
    <span className={`${cmsTextClass} min-w-0 pt-1.5 text-ink-700`}>{children}</span>
  </li>
);

Item.propTypes = {
  icon: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/**
 * Site1 contact band: how to reach the business, and when it is open.
 *
 * Every line is optional and the caller passes null for what was left blank;
 * a block with nothing in it is not rendered, and the caller omits the whole
 * section when both are empty. One block spans the band on its own rather than
 * sitting in half of it.
 */
const ContactSection = ({ address = null, phone = null, email = null, openingHours = null }) => {
  const { t } = useTranslation();
  const hasContact = Boolean(address || phone || email);

  return (
    <section id="contact" aria-labelledby="site1-contact-title" className="scroll-mt-20 bg-ink-50 py-12 md:py-16">
      <h2 id="site1-contact-title" className="sr-only">
        {t("BookingPage.menu.contact")}
      </h2>
      <div className={sheetClass}>
        <div className="mx-auto grid max-w-4xl gap-10 [grid-template-columns:repeat(auto-fit,minmax(min(16rem,100%),1fr))] md:gap-16">
          {hasContact && (
            <div className="min-w-0">
              <h3 className={eyebrowClass}>{t("BookingPage.footer.contact")}</h3>
              <ul className="m-0 mt-5 flex list-none flex-col gap-4 p-0">
                {address && <Item icon="bi-geo-alt">{address}</Item>}
                {phone && (
                  <Item icon="bi-telephone">
                    {/* tel:/mailto: are built from the stored value, so a phone
                        with spaces still dials correctly. */}
                    <a className={linkClass} href={`tel:${phone.replace(/\s+/g, "")}`}>
                      {phone}
                    </a>
                  </Item>
                )}
                {email && (
                  <Item icon="bi-envelope">
                    <a className={linkClass} href={`mailto:${email}`}>
                      {email}
                    </a>
                  </Item>
                )}
              </ul>
            </div>
          )}

          {openingHours && (
            <div className="min-w-0">
              <h3 className={eyebrowClass}>{t("BookingPage.footer.openingHours")}</h3>
              <ul className="m-0 mt-5 list-none p-0">
                {/* Free text, one line per day: honour the operator's line
                    breaks without letting them inject markup. */}
                <Item icon="bi-clock">
                  <span className="whitespace-pre-line">{openingHours}</span>
                </Item>
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

ContactSection.propTypes = {
  address: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  openingHours: PropTypes.string,
};

export default ContactSection;
